
import { Badkamercore_ProjectType, CrudAction, ProductPackage } from '@/__generated__/globalTypes'
import AddProductPackageDialog from '@/components/projectProductPackages/AddProductPackageDialog.vue'
import CopyProductPackageDialog from '@/components/projectProductPackages/CopyProductPackageDialog.vue'
import ProductPackageUploadImportDialog from '@/components/productPackageUploadImportDialog/ProductPackageUploadImportDialog.vue'
import QUERIES from '@/queries/queries'
import { DataTableHeader } from 'vuetify'
import { RouteNames } from '@/router/routeNames'
import { Component, Vue } from 'vue-property-decorator'
import {
  DeleteProductPackageDocument,
  DeleteProductPackageMutation,
  DeleteProductPackageMutationVariables
} from './gql/__generated__/DeleteProductPackage.mutation'
import {
  ProductPackageListDocument,
  ProductPackageListQuery,
  ProductPackageListQueryVariables
} from './gql/__generated__/GetProductPackageList.query'
import {
  GetProductPackagePageDocument,
  GetProductPackagePageQuery,
  GetProductPackagePageQueryVariables
} from './gql/__generated__/GetProductPackagePage.query'

@Component({
  components: {
    AddProductPackageDialog,
    CopyProductPackageDialog,
    ProductPackageUploadImportDialog
  }
})
export default class ProductPackageListView extends Vue {
  POLL_INTERVAL = 3000

  headers: DataTableHeader[] = [
    { text: '', value: 'isMain', sortable: true, align: 'center', cellClass: 'c-productpackage_listpage__icon-column' },
    {
      text: '',
      align: 'start',
      sortable: false,
      value: 'name'
    },
    { text: '', value: 'actions', sortable: false, align: 'end' }
  ]

  productPackages: ProductPackage[] | null = null

  RouteNames = RouteNames
  Badkamercore_ProjectType = Badkamercore_ProjectType

  showProductPackageAddModal = false

  showUpdateProductPackageDialog = false
  productPackageToUpdateId = ''
  productPackageToUpdateName = ''
  CrudAction = CrudAction

  // Data for copying a product package
  showProductPackageCopyModal = false
  productPackageToCopyId = ''
  productPackageToCopyName = ''

  downloadsInProgress: Record<string, boolean> = {}

  created() {
    this.fetchData()
  }

  get isNotAdmin() {
    return !this.$store.getters.isAdmin
  }

  get productPackageNames() {
    return this.productPackages?.map((x) => x.name)
  }

  assignRowClasses(item: ProductPackage) {
    return { 'c-productpackage_listpage__row': item.isBeingUpdated }
  }

  fetchData() {
    this.$apollo.addSmartQuery<ProductPackageListQuery, ProductPackageListQueryVariables>(
      QUERIES.ProductPackageListViewQuery,
      {
        query: ProductPackageListDocument,
        update: (data) => data,
        fetchPolicy: 'network-only',
        result: (result) => {
          this.productPackages = result.data?.productPackages as ProductPackage[]
          this.syncPolling()
        },
        error: (error) => {
          this.$store.dispatch('showErrorDialog', {
            Code: 'E4150',
            Message: error
          })
        }
      }
    )
  }

  syncPolling() {
    const isAnyProductPackageBeingUpdated = this.productPackages?.some((x) => x.isBeingUpdated)

    if (isAnyProductPackageBeingUpdated) {
      this.$apollo.queries[QUERIES.ProductPackageListViewQuery].startPolling(this.POLL_INTERVAL)
    } else {
      this.$apollo.queries[QUERIES.ProductPackageListViewQuery].stopPolling()
    }
  }

  confirmDeleteProductPackage(id: string) {
    this.$showConfirmationDialog({
      awaitConfirm: true,
      Callback: async () => {
        await this.deleteProductPackage(id)
        this.$hideConfirmationDialog()
      },
      Code: 'C4016'
    })
  }

  async deleteProductPackage(id: string) {
    await this.$apolloMutate<DeleteProductPackageMutation, DeleteProductPackageMutationVariables>({
      mutation: DeleteProductPackageDocument,
      variables: {
        productPackageId: id
      },
      refetchQueries: [
        {
          query: ProductPackageListDocument
        }
      ],
      error: 'E4200'
    })
  }

  navigateToCms(id: string) {
    this.$apollo
      .query<GetProductPackagePageQuery, GetProductPackagePageQueryVariables>({
        query: GetProductPackagePageDocument,
        variables: {
          aliasId: id
        }
      })
      .catch((reason) => {
        this.$store.dispatch('showErrorDialog', {
          Code: 'E4151',
          Message: reason
        })
      })
      .then((response) => {
        if (response) {
          const host = process.env.VUE_APP_CMS_URL
          const contentItemId = response.data.productPackagePage?.[0]?.contentItemId
          if (contentItemId) {
            const url = `${host}/Admin/Contents/ContentItems/${contentItemId}/Edit?returnUrl=%2FAdmin%2FContents%2FContentItems`
            window.open(url, 'editProductPackageInCms')
          } else {
            this.$store.dispatch('showErrorDialog', {
              Code: 'E4152'
            })
          }
        }
      })
  }

  openUpdateProductPackageDialog(item: ProductPackage) {
    this.showUpdateProductPackageDialog = true
    this.productPackageToUpdateId = item.id
    this.productPackageToUpdateName = item.name
  }

  onUpdateFileUploaded() {
    this.showUpdateProductPackageDialog = false
    this.$router.push({
      name: RouteNames.PRODUCT_PACKAGE_IMPORTS,
      params: { propositionType: Badkamercore_ProjectType.Productenpropositie }
    })
  }

  openProductPackageCopyModal(productPackage: ProductPackage) {
    this.showProductPackageCopyModal = true
    this.productPackageToCopyId = productPackage.id
    this.productPackageToCopyName = productPackage.name + this.$t('productPackage.copy.form.defaultFieldValue')
  }

  // TODO: move according to PBI #142997
  // downloadImportFile(productPackage: ProductPackage) {
  //   this.$set(this.downloadsInProgress, productPackage.id, true)

  //   this.$fileApi
  //     .downloadFile(`/importProductPackage/${productPackage.id}/${productPackage.name}`, productPackage.name)
  //     .catch((error) => {
  //       this.$store.dispatch('showErrorDialog', {
  //         Code: 'E4244',
  //         Message: error
  //       })
  //     })
  //     .finally(() => {
  //       this.$set(this.downloadsInProgress, productPackage.id, false)
  //     })
  // }

  downloadProductPackageDataFile(productPackage: ProductPackage) {
    this.$set(this.downloadsInProgress, productPackage.id, true)

    this.$fileApi
      .downloadFile(`/productPackageDataFile/${productPackage.id}`, productPackage.name)
      .catch((error) => {
        this.$store.dispatch('showErrorDialog', {
          Code: 'E4252',
          Message: error
        })
      })
      .finally(() => {
        this.$set(this.downloadsInProgress, productPackage.id, false)
      })
  }
}
